import React, { useCallback, useMemo, useState } from 'react';
import AlertBanner from '../../components/common/AlertBanner/AlertBanner';
import Button from '../../components/common/Button/Button';
import ClearFiltersButton from '../../components/common/ClearFiltersButton/ClearFiltersButton';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import DataTable from '../../components/common/DataTable/DataTable';
import DatePresetFilter from '../../components/common/DatePresetFilter/DatePresetFilter';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import ExpandableDetails from '../../components/common/ExpandableDetails/ExpandableDetails';
import FilterBar from '../../components/common/FilterBar/FilterBar';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import FormTextarea from '../../components/common/FormTextarea/FormTextarea';
import ListingBottomRow from '../../components/common/ListingBottomRow/ListingBottomRow';
import Loading from '../../components/common/Loading/Loading';
import PageContainer from '../../components/common/PageContainer/PageContainer';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import SingleSelectFilter from '../../components/common/SingleSelectFilter/SingleSelectFilter';
import TabBar from '../../components/common/TabBar/TabBar';
import Tooltip from '../../components/common/Tooltip';
import SysBadge from '../../components/common/SysListing/SysBadge';
import styles from '../../components/common/SysListing/SysListing.module.css';
import {
  SYS_DATE_PRESET_OPTIONS,
  getSysDateDefaultRange,
  isSysDateDefaultRange,
} from '../../constants/datePresets';
import { usePageSize } from '../../hooks/usePageSize';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import { usePaymentHistoryErrors } from '../../hooks/usePaymentHistoryErrors';
import { usePaymentWebhookFailures } from '../../hooks/usePaymentWebhookFailures';
import { PaymentWebhookFailureService } from '../../services/PaymentWebhookFailureService';
import {
  GATEWAY_PROVIDERS,
  PAYMENT_ERROR_PROVIDER_OPTIONS,
  PAYMENT_SYSTEM_LABELS,
  type GatewayProvider,
  type PaymentHistoryErrorFilters,
  type PaymentSystem,
  type PaymentWebhookFailure,
  type PaymentWebhookFailureFilters,
  type PaymentWebhookFailureListStatus,
} from '../../types/payment';
import { formatCurrency, formatDateTime } from '../../utils';

const DEFAULT_DATE_RANGE = getSysDateDefaultRange();
const PROVIDER_VALUES = PAYMENT_ERROR_PROVIDER_OPTIONS.map(({ value }) => value);
const GATEWAY_VALUES = [...GATEWAY_PROVIDERS];
const WEBHOOK_STATUS_OPTIONS = [
  { value: 'open', label: 'Pendentes' },
  { value: 'recovered', label: 'Resolvidas' },
] as const;
const PAYMENT_ERROR_TABS = [
  { key: 'charges', label: 'Falhas de cobrança' },
  { key: 'webhooks', label: 'Falhas de webhook' },
] as const;

type PaymentErrorTab = (typeof PAYMENT_ERROR_TABS)[number]['key'];

const FILTER_SCHEMA = {
  tab: queryFilter.enum<PaymentErrorTab>('charges', ['charges', 'webhooks']),
  page: queryFilter.integer(1, { min: 1 }),
  search: queryFilter.string(),
  paymentId: queryFilter.string(),
  provider: queryFilter.enum<PaymentSystem | ''>('', ['', ...PROVIDER_VALUES]),
  status: queryFilter.enum<PaymentWebhookFailureListStatus>('open', ['open', 'pending', 'processing', 'recovered']),
  dateFrom: queryFilter.string(DEFAULT_DATE_RANGE.start),
  dateTo: queryFilter.string(DEFAULT_DATE_RANGE.end),
};

function getProviderLabel(provider: string): string {
  if (provider in PAYMENT_SYSTEM_LABELS) {
    return PAYMENT_SYSTEM_LABELS[provider as PaymentSystem];
  }
  return provider;
}

function getProviderTone(provider: string): string {
  if (provider === 'asaas' || provider === 'stripe' || provider === 'paypal') {
    return provider;
  }
  return '';
}

function getFailurePayload(row: PaymentWebhookFailure): string {
  if (row.payload_json && Object.keys(row.payload_json).length > 0) {
    return JSON.stringify(row.payload_json, null, 2);
  }

  return row.raw_body?.trim() || '';
}

const PaymentErrors: React.FC = () => {
  const chargePaging = usePageSize('payment_history_errors_page_size', 10);
  const webhookPaging = usePageSize('payment_webhook_failures_page_size', 10);
  const { filters: queryFilters, setFilter, setFilters, resetFilters } = useQueryFilters(
    FILTER_SCHEMA,
    { pageKey: 'page' },
  );
  const {
    tab,
    search,
    paymentId,
    provider: selectedProvider,
    status: selectedStatus,
    dateFrom,
    dateTo,
  } = queryFilters;
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PaymentWebhookFailure | null>(null);
  const [editingRow, setEditingRow] = useState<PaymentWebhookFailure | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  const handleClearFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const handleDateChange = useCallback((start: string, end: string) => {
    setFilters({ dateFrom: start, dateTo: end });
  }, [setFilters]);

  const chargeFilters: PaymentHistoryErrorFilters = useMemo(() => ({
    search: search || undefined,
    paymentId: paymentId || undefined,
    provider: selectedProvider || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  }), [dateFrom, dateTo, paymentId, search, selectedProvider]);

  const webhookFilters: PaymentWebhookFailureFilters = useMemo(() => ({
    search: search || undefined,
    paymentId: paymentId || undefined,
    provider: GATEWAY_VALUES.includes(selectedProvider as GatewayProvider)
      ? selectedProvider as GatewayProvider
      : undefined,
    status: selectedStatus,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  }), [dateFrom, dateTo, paymentId, search, selectedProvider, selectedStatus]);

  const chargeState = usePaymentHistoryErrors(
    chargePaging.page,
    chargePaging.pageSize,
    chargeFilters,
  );
  const webhookState = usePaymentWebhookFailures(
    webhookPaging.page,
    webhookPaging.pageSize,
    webhookFilters,
  );

  const isWebhookTab = tab === 'webhooks';
  const listing = isWebhookTab ? webhookState : chargeState;
  const paging = isWebhookTab ? webhookPaging : chargePaging;
  const hasActiveFilters = !!search
    || !!paymentId
    || !!selectedProvider
    || (isWebhookTab && selectedStatus !== 'open')
    || !isSysDateDefaultRange(dateFrom, dateTo);

  const handleReprocess = async (id: number) => {
    setActionError(null);
    setBusyId(id);
    try {
      await PaymentWebhookFailureService.reprocess(id);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Não foi possível reprocessar o evento.');
    } finally {
      webhookState.refetch();
      setBusyId(null);
    }
  };

  const handleOpenEdit = (row: PaymentWebhookFailure) => {
    setActionError(null);
    setEditError(null);
    setEditingRow(row);
    setEditDraft(getFailurePayload(row));
  };

  const handleSavePayload = async () => {
    if (!editingRow) {
      return;
    }

    try {
      const parsed = JSON.parse(editDraft);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setEditError('O JSON deve ser um objeto.');
        return;
      }
    } catch {
      setEditError('JSON inválido.');
      return;
    }

    setEditError(null);
    setBusyId(editingRow.id);
    try {
      await PaymentWebhookFailureService.updatePayload(editingRow.id, editDraft);
      setEditingRow(null);
      webhookState.refetch();
    } catch (error) {
      setEditError(error instanceof Error ? error.message : 'Não foi possível salvar o JSON.');
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }

    setActionError(null);
    setBusyId(pendingDelete.id);
    try {
      await PaymentWebhookFailureService.remove(pendingDelete.id);
      setPendingDelete(null);
      webhookState.refetch();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Não foi possível excluir o evento.');
      setPendingDelete(null);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <PageContainer className={styles.page}>
      <TabBar
        tabs={PAYMENT_ERROR_TABS}
        activeKey={tab}
        onTabChange={(key) => setFilter('tab', key as PaymentErrorTab)}
      />

      <FilterBar gap="sm" pushFirstChild>
        <RecordCount
          label="Erros:"
          count={`${listing.pagination.totalRecords} registros`}
          className={styles['record-count']}
        />
        <FilterGroup>
          <SearchFilterBar
            searchValue={search}
            onSearchChange={(value) => setFilter('search', value)}
            searchPlaceholder="Pesquisar"
            loading={listing.loading}
          />
          <SearchFilterBar
            searchValue={paymentId}
            onSearchChange={(value) => setFilter('paymentId', value)}
            searchPlaceholder="ID da cobrança"
            loading={listing.loading}
          />
          <DatePresetFilter
            dateStart={dateFrom}
            dateEnd={dateTo}
            onChange={handleDateChange}
            placeholder="Período"
            presetOptions={SYS_DATE_PRESET_OPTIONS}
          />
          <SingleSelectFilter
            value={selectedProvider}
            onChange={(value) => setFilter('provider', value as PaymentSystem | '')}
            options={isWebhookTab
              ? PAYMENT_ERROR_PROVIDER_OPTIONS.filter((option) => GATEWAY_VALUES.includes(option.value as GatewayProvider))
              : PAYMENT_ERROR_PROVIDER_OPTIONS}
            placeholder="Gateway"
          />
          {isWebhookTab && (
            <SingleSelectFilter
              value={selectedStatus}
              onChange={(value) => setFilter('status', value as PaymentWebhookFailureListStatus)}
              options={[...WEBHOOK_STATUS_OPTIONS]}
              placeholder="Status"
            />
          )}
        </FilterGroup>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={handleClearFilters} />
        )}
      </FilterBar>

      {actionError && (
        <AlertBanner message={actionError} variant="error" />
      )}

      {listing.loading ? (
        <Loading />
      ) : listing.error ? (
        <EmptyState message={`Erro: ${listing.error}`} />
      ) : listing.rows.length === 0 ? (
        <EmptyState message={isWebhookTab
          ? 'Nenhuma falha de webhook encontrada com os filtros selecionados.'
          : 'Nenhum erro de pagamento encontrado com os filtros selecionados.'}
        />
      ) : isWebhookTab ? (
        <DataTable
          headers={[
            'Data e hora',
            'Gateway',
            'Cobrança',
            'Evento / código',
            'Mensagem',
            'Ações',
          ]}
          gridTemplate="160px 110px minmax(140px, 0.9fr) minmax(140px, 0.9fr) minmax(220px, 1.4fr) 120px"
          highlightTerm={search}
          lastColumnIsActions
        >
          {webhookState.rows.map((row) => {
            const paymentLabel = row.external_payment_id || row.provider_event_id || '—';
            const payload = getFailurePayload(row);
            const canRetry = row.status === 'pending' || row.status === 'processing';

            return (
              <DataTable.Row key={row.id}>
                <div className={styles['date-cell']}>
                  <span className={styles['date-text']}>{formatDateTime(row.received_at)}</span>
                </div>

                <div className={styles['provider-cell']}>
                  <SysBadge tone={getProviderTone(row.provider)}>
                    {getProviderLabel(row.provider)}
                  </SysBadge>
                </div>

                <div className={styles['meta-cell']}>
                  <div className={styles['meta-stack']}>
                    <span className={styles['meta-primary']} title={paymentLabel}>
                      {paymentLabel}
                    </span>
                    <span className={styles['meta-secondary']}>
                      {[row.student_name, `${row.attempt_count} tentativa${row.attempt_count === 1 ? '' : 's'}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                </div>

                <div className={styles['meta-cell']}>
                  <div className={styles['meta-stack']}>
                    <span className={styles['meta-primary']} title={row.gateway_event ?? undefined}>
                      {row.gateway_event || '—'}
                    </span>
                    <span className={styles['meta-secondary']}>
                      {row.failure_code || row.status}
                    </span>
                  </div>
                </div>

                <div className={styles['message-cell']}>
                  <ExpandableDetails
                    summary={row.last_error_message || row.failure_reason}
                    sections={[
                      { label: 'Mensagem', content: row.last_error_message || row.failure_reason },
                      { label: 'Status', content: row.status },
                      payload ? { label: 'Payload', content: payload } : null,
                    ]}
                  />
                </div>

                <div className={styles['actions-cell']}>
                  <Tooltip content="Reprocessar">
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      icon="replay"
                      aria-label="Reprocessar"
                      disabled={!canRetry || busyId === row.id}
                      onClick={() => handleReprocess(row.id)}
                    />
                  </Tooltip>
                  <Tooltip content="Editar JSON">
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      icon="edit"
                      aria-label="Editar JSON"
                      disabled={!canRetry || busyId === row.id}
                      onClick={() => handleOpenEdit(row)}
                    />
                  </Tooltip>
                  <Tooltip content="Excluir">
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      icon="delete"
                      aria-label="Excluir"
                      disabled={busyId === row.id}
                      onClick={() => setPendingDelete(row)}
                    />
                  </Tooltip>
                </div>
              </DataTable.Row>
            );
          })}
        </DataTable>
      ) : (
        <DataTable
          headers={[
            'Data e hora',
            'Gateway',
            'Cobrança',
            'Evento / código',
            'Mensagem',
          ]}
          gridTemplate="160px 110px minmax(140px, 0.9fr) minmax(140px, 0.9fr) minmax(240px, 1.4fr)"
          highlightTerm={search}
          lastColumnIsActions={false}
        >
          {chargeState.rows.map((row) => {
            const paymentLabel = row.external_payment_id
              || (row.payment_history_id ? `#${row.payment_history_id}` : '—');
            const valueLabel = row.payment_value != null
              ? formatCurrency(row.payment_value, row.payment_currency ?? 'BRL')
              : null;
            const statusContent = [
              row.raw_status ? `raw: ${row.raw_status}` : null,
              row.normalized_status ? `interno: ${row.normalized_status}` : null,
            ].filter(Boolean).join('\n');
            const payload = row.error_payload && Object.keys(row.error_payload).length > 0
              ? JSON.stringify(row.error_payload, null, 2)
              : '';

            return (
              <DataTable.Row key={row.id}>
                <div className={styles['date-cell']}>
                  <span className={styles['date-text']}>{formatDateTime(row.created_at)}</span>
                </div>

                <div className={styles['provider-cell']}>
                  <SysBadge tone={getProviderTone(row.provider)}>
                    {getProviderLabel(row.provider)}
                  </SysBadge>
                </div>

                <div className={styles['meta-cell']}>
                  <div className={styles['meta-stack']}>
                    <span className={styles['meta-primary']} title={paymentLabel}>
                      {paymentLabel}
                    </span>
                    <span className={styles['meta-secondary']}>
                      {[row.payment_status, valueLabel].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </div>
                </div>

                <div className={styles['meta-cell']}>
                  <div className={styles['meta-stack']}>
                    <span className={styles['meta-primary']} title={row.gateway_event ?? undefined}>
                      {row.gateway_event || '—'}
                    </span>
                    <span className={styles['meta-secondary']}>
                      {row.error_code || row.normalized_status || row.raw_status || '—'}
                    </span>
                  </div>
                </div>

                <div className={styles['message-cell']}>
                  <ExpandableDetails
                    summary={row.error_message}
                    sections={[
                      { label: 'Mensagem', content: row.error_message },
                      row.error_kind ? { label: 'Tipo', content: row.error_kind } : null,
                      statusContent ? { label: 'Status', content: statusContent } : null,
                      payload ? { label: 'Payload', content: payload } : null,
                    ]}
                  />
                </div>
              </DataTable.Row>
            );
          })}
        </DataTable>
      )}

      {listing.rows.length > 0 && (
        <ListingBottomRow
          pageSize={paging.pageSize}
          pageSizeOptions={paging.pageSizeOptions}
          onPageSizeChange={paging.handlePageSizeChange}
          pagination={listing.pagination}
          loading={listing.loading}
          onPageChange={paging.handlePageChange}
        />
      )}

      <ConfirmModal
        open={editingRow !== null}
        title="Editar JSON"
        message="Altere o payload e salve antes de reprocessar."
        confirmLabel="Salvar"
        cancelLabel="Cancelar"
        variant="info"
        icon="edit"
        width={720}
        loading={editingRow !== null && busyId === editingRow.id}
        onConfirm={() => void handleSavePayload()}
        onCancel={() => {
          if (busyId === editingRow?.id) {
            return;
          }
          setEditingRow(null);
          setEditError(null);
        }}
      >
        <div className={styles['modal-form']}>
          <FormTextarea
            id="payment-webhook-failure-json"
            label="Payload"
            value={editDraft}
            onChange={(event) => {
              setEditDraft(event.target.value);
              setEditError(null);
            }}
            error={editError ?? undefined}
            autoResize={false}
            rows={16}
            spellCheck={false}
          />
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={pendingDelete !== null}
        title="Excluir evento"
        message="Excluir este evento de recuperação? Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        icon="delete"
        loading={pendingDelete !== null && busyId === pendingDelete.id}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => {
          if (busyId === pendingDelete?.id) {
            return;
          }
          setPendingDelete(null);
        }}
      />
    </PageContainer>
  );
};

export default PaymentErrors;
